import { type JSX } from 'react';
import { useMediaQuery } from 'react-responsive';
import { cx } from '@linaria/core';
import { Toolbar, ToolbarItem, useToolbarState } from 'reakit';

import { isDesktopQuery } from '@cx-modules/react-utils';

import { Scrollable } from '../Scrollable';

import {
  ScrollToolBarItemContainer,
  styles,
  TabContainer,
  TabNavScrollInner,
  TabNavScrollOuter,
  TabNavWrapper,
  TabNavWrapperInner,
  TabWithScrollButtonsContainer,
  ToolBarItemContainer,
  ToolbarItemWithNewIcon,
  WrapperWithNewIcon,
} from './style';

export type TabNavType = 'text' | 'pill';

export interface TabNavItem {
  className?: string;
  dataTrack?: string;
  icon?: JSX.Element;
  id: string;
  name: string;
}

export interface TabNavProps {
  activeItemId?: string;
  ariaLabel: string;
  className?: string;
  disableScrollSnap?: boolean;
  hasNewItems?: boolean;
  id: string;
  items: TabNavItem[];
  onClick(item: TabNavItem): void;
  type: TabNavType;
}

export const TabNav = ({
  activeItemId,
  ariaLabel,
  className,
  disableScrollSnap,
  hasNewItems,
  id,
  items = [],
  onClick,
  type = 'text',
}: TabNavProps): JSX.Element => {
  const toolbar = useToolbarState({ baseId: id, loop: true });
  const isDesktop = useMediaQuery(isDesktopQuery);

  function handleOnClick(item: TabNavItem, index: number) {
    if (!isDesktop && !disableScrollSnap) {
      const elementToScrollTo = document.getElementById(`${id}-${index + 1}`);
      window.setTimeout(() => {
        elementToScrollTo?.scrollIntoView({
          block: 'nearest',
          inline: 'center',
        });
      }, 0);
    }
    onClick(item);
  }

  return (
    <TabNavWrapper className={cx(className, !!hasNewItems && WrapperWithNewIcon)}>
      <TabNavWrapperInner>
        <TabNavScrollOuter>
          <TabNavScrollInner>
            <Toolbar {...toolbar} aria-label={ariaLabel} className={cx(TabContainer)}>
              {items.map((item, index) => {
                const { className, dataTrack, icon, id: itemId, name } = item;
                const style = getItemStyle(activeItemId === itemId, type);
                return (
                  <ToolBarItemContainer className={cx(!!hasNewItems && ToolbarItemWithNewIcon)} key={itemId}>
                    <ToolbarItem
                      key={itemId}
                      {...toolbar}
                      className={cx(style, className)}
                      data-track={dataTrack}
                      onClick={() => {
                        handleOnClick(item, index);
                      }}
                    >
                      {name} {icon ? icon : null}
                    </ToolbarItem>
                  </ToolBarItemContainer>
                );
              })}
            </Toolbar>
          </TabNavScrollInner>
        </TabNavScrollOuter>
      </TabNavWrapperInner>
    </TabNavWrapper>
  );
};

export interface TabNavWithScrollButtons extends TabNavProps {
  innerWrapperClass?: string;
  leftBtnClass?: string;
  leftBtnDataTrack?: string;
  outerWrapperClass?: string;
  rightBtnClass?: string;
  rightBtnDataTrack?: string;
  scrollIncrement?: number;
}

export const TabNavWithScrollButtons = ({
  activeItemId,
  ariaLabel,
  className,
  hasNewItems,
  id,
  innerWrapperClass,
  items = [],
  leftBtnClass,
  leftBtnDataTrack,
  onClick,
  outerWrapperClass,
  rightBtnClass,
  rightBtnDataTrack,
  scrollIncrement,
  type = 'text',
}: TabNavWithScrollButtons): JSX.Element => {
  const toolbar = useToolbarState({ baseId: id, loop: true });

  return (
    <TabNavWrapper className={cx(className, !!hasNewItems && WrapperWithNewIcon)}>
      <Scrollable
        buttonsWithFade
        innerClass={innerWrapperClass ? innerWrapperClass : ''}
        leftBtnDataTrack={leftBtnDataTrack}
        leftButtonClass={leftBtnClass ? leftBtnClass : ''}
        outerClass={outerWrapperClass ? outerWrapperClass : ''}
        rightBtnDataTrack={rightBtnDataTrack}
        rightButtonClass={rightBtnClass ? rightBtnClass : ''}
        scrollIncrement={scrollIncrement}
      >
        <Toolbar {...toolbar} aria-label={ariaLabel} className={cx(TabWithScrollButtonsContainer)}>
          {items.map(item => {
            const { className, dataTrack, icon, id: itemKey, name } = item;
            const style = getItemStyle(activeItemId === itemKey, type);
            const itemId = `${id}_${name}_${itemKey}`.replace(/\s+/g, '-');
            return (
              <ScrollToolBarItemContainer className={cx(!!hasNewItems && ToolbarItemWithNewIcon)} key={itemKey}>
                <ToolbarItem
                  key={itemKey}
                  {...toolbar}
                  className={cx(style, className)}
                  id={itemId}
                  onClick={() => {
                    onClick(item);
                  }}
                  {...(dataTrack ? { 'data-track': dataTrack } : {})}
                >
                  {name} {icon ? icon : null}
                </ToolbarItem>
              </ScrollToolBarItemContainer>
            );
          })}
        </Toolbar>
      </Scrollable>
    </TabNavWrapper>
  );
};

function getItemStyle(isActive: boolean, type: TabNavType) {
  const tabNavStyles = styles as Record<string, any>;
  return [tabNavStyles.tabNav, tabNavStyles[type], ...(isActive ? [tabNavStyles[`${type}Active`]] : [])].join(' ');
}
